'use-strict';

import PhotoSwipeLightbox from './photoswipe-lightbox.esm.min.js';
import Photoswipe from './photoswipe.esm.min.js';
// js gallery
class ZoomAction extends HTMLElement {
  constructor() {
    super();
    this.type = this.dataset.type || 'no_zoom';
    this.zoomOption = this.dataset.zoomOption || 'external';
    this.lightbox = null;
    this.drift = null;
    this.drifts = [];
    this._pointerStart = null;
    this._lastTap = null;
    this._lastOpenAt = 0;
    this._onResize = this.responsive.bind(this);
    this._onClick = this.handleClick.bind(this);
    this._onDblClick = this.handleDblClick.bind(this);
    this._onPointerDown = this.handlePointerDown.bind(this);
    this._onPointerUp = this.handlePointerUp.bind(this);
    this.init();
  }
  init() {
    if (this.type === 'no_zoom') return;
    this.addEventListener('click', this._onClick);
    this.addEventListener('dblclick', this._onDblClick);
    this.addEventListener('pointerdown', this._onPointerDown);
    this.addEventListener('pointerup', this._onPointerUp);
    this.responsive();
    window.addEventListener('resize', this._onResize);
    if (document.readyState !== 'complete') {
      window.addEventListener('load', this._onResize);
    }
  }
  canOpenLightbox() {
    return this.type !== 'no_zoom';
  }
  shouldUseDrift() {
    if (this.type === 'no_zoom' || this.type === 'open_lightbox') return false;
    return (
      window.innerWidth >= 1025 &&
      (!window.matchMedia ||
        !window.matchMedia('(hover: none), (pointer: coarse)').matches)
    );
  }
  findGallerySlide(target) {
    if (!target || typeof target.closest !== 'function') return null;
    const slide = target.closest(
      'a.media-gallery__image, .media-gallery__image[data-pswp-src]'
    );
    if (!slide || !this.contains(slide) || slide.closest('.media-thumb-swiper')) {
      return null;
    }
    return slide;
  }
  handleClick(e) {
    const anchor = e.target.closest('a.media-gallery__image');
    if (anchor && this.contains(anchor)) {
      e.preventDefault();
    }
    const slide = this.findGallerySlide(e.target);
    if (slide && e.target.closest('.zoom_light_box') && this.canOpenLightbox()) {
      this._lastTap = null;
      this.openLightboxForAnchor(slide, { x: e.clientX, y: e.clientY });
    }
  }
  handleDblClick(e) {
    const slide = this.findGallerySlide(e.target);
    if (!slide || !this.canOpenLightbox()) return;
    if (e.target.closest('.zoom_light_box')) return;
    e.preventDefault();
    this._lastTap = null;
    this.openLightboxForAnchor(slide, { x: e.clientX, y: e.clientY });
  }
  handlePointerDown(e) {
    const slide = this.findGallerySlide(e.target);
    if (!slide || e.target.closest('.zoom_light_box')) {
      this._pointerStart = null;
      return;
    }
    this._pointerStart = {
      x: e.clientX,
      y: e.clientY,
      time: Date.now(),
      anchor: slide,
    };
  }
  handlePointerUp(e) {
    const slide = this.findGallerySlide(e.target);
    if (
      !slide ||
      !this.canOpenLightbox() ||
      e.target.closest('.zoom_light_box')
    ) {
      return;
    }
    if (
      this._pointerStart &&
      Math.hypot(
        e.clientX - this._pointerStart.x,
        e.clientY - this._pointerStart.y
      ) > 14
    ) {
      this._lastTap = null;
      return;
    }
    const now = Date.now();
    if (
      this._lastTap &&
      this._lastTap.anchor === slide &&
      now - this._lastTap.time <= 380 &&
      Math.hypot(e.clientX - this._lastTap.x, e.clientY - this._lastTap.y) <= 48
    ) {
      this._lastTap = null;
      this.openLightboxForAnchor(slide, { x: e.clientX, y: e.clientY });
    } else {
      this._lastTap = { time: now, x: e.clientX, y: e.clientY, anchor: slide };
    }
  }
  openLightboxForAnchor(anchor, pointerPos) {
    if (window.pswp || Date.now() - this._lastOpenAt < 450) return;
    this._lastOpenAt = Date.now();
    const driftTrigger = anchor.querySelector('.drift-trigger');
    if (driftTrigger) {
      driftTrigger.dispatchEvent(new MouseEvent('mouseleave', { bubbles: true }));
    }
    this.initLightBox();
    if (!this.lightbox) return;
    const anchorItems = Array.from(
      this.querySelectorAll(
        'a.media-gallery__image:not(.swiper-slide-duplicate)'
      )
    );
    const pos =
      pointerPos && (pointerPos.x || pointerPos.y) ? pointerPos : null;
    if (anchorItems.length > 0 && anchor.tagName === 'A') {
      const index = Math.max(0, anchorItems.indexOf(anchor));
      this.lightbox.loadAndOpen(index, { gallery: this }, pos);
      return;
    }
    const divItems = Array.from(
      this.querySelectorAll(
        '.media-gallery__image[data-pswp-src]:not(.swiper-slide-duplicate)'
      )
    );
    if (divItems.length > 0) {
      const dataSource = divItems.map((el) => {
        const img = el.querySelector('img');
        return {
          src: el.dataset.pswpSrc,
          width: parseInt(el.dataset.pswpWidth, 10) || 1600,
          height: parseInt(el.dataset.pswpHeight, 10) || 1600,
          msrc: img ? img.currentSrc || img.src : undefined,
          alt: img ? img.getAttribute('alt') || '' : '',
          element: el,
          thumbCropped: true,
        };
      });
      const index = Math.max(0, divItems.indexOf(anchor));
      this.lightbox.loadAndOpen(index, dataSource, pos);
    }
  }
  initLightBox() {
    if (this.lightbox !== null) return;
    this.lightbox = new PhotoSwipeLightbox({
      gallery: this,
      children: 'a.media-gallery__image:not(.swiper-slide-duplicate)',
      pswpModule: () => Photoswipe,
      counter: false,
      zoom: false,
      preloader: false,
      arrowPrev: false,
      arrowNext: false,
      close: false,
      loop: false,
      bgOpacity: 1,
      wheelToZoom: true,
      maxZoomLevel: 3,
      secondaryZoomLevel: 2,
    });
    this.lightbox.on('uiRegister', () => {
      const { pswp } = this.lightbox;
      pswp?.on('close', () => {
        if (document.fullscreenElement && document.exitFullscreen) {
          document.exitFullscreen().catch(() => {});
        } else if (
          document.webkitFullscreenElement &&
          document.webkitExitFullscreen
        ) {
          document.webkitExitFullscreen();
        }
      });
      pswp?.ui.registerElement({
        name: 'bls--zoom',
        order: 10,
        isButton: true,
        tagName: 'button',
        html: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line><line x1="11" y1="8" x2="11" y2="14"></line><line x1="8" y1="11" x2="14" y2="11"></line></svg>',
        onInit: (el) => {
          el.setAttribute('aria-label', 'Zoom in or out');
          el.setAttribute('title', 'Zoom in or out');
        },
        onClick: () => {
          pswp.currSlide?.toggleZoom();
        },
      });
      if (document.fullscreenEnabled || document.webkitFullscreenEnabled) {
        pswp?.ui.registerElement({
          name: 'bls--fullscreen',
          order: 15,
          isButton: true,
          tagName: 'button',
          html: '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 3 21 3 21 9"></polyline><polyline points="9 21 3 21 3 15"></polyline><line x1="21" y1="3" x2="14" y2="10"></line><line x1="3" y1="21" x2="10" y2="14"></line></svg>',
          onInit: (el) => {
            el.setAttribute('aria-label', 'Toggle full screen');
            el.setAttribute('title', 'Full screen');
          },
          onClick: () => {
            const rootEl = pswp.element || document.documentElement;
            if (
              !document.fullscreenElement &&
              !document.webkitFullscreenElement
            ) {
              if (rootEl.requestFullscreen) {
                rootEl.requestFullscreen().catch(() => {});
              } else if (rootEl.webkitRequestFullscreen) {
                rootEl.webkitRequestFullscreen();
              }
            } else if (document.exitFullscreen) {
              document.exitFullscreen().catch(() => {});
            } else if (document.webkitExitFullscreen) {
              document.webkitExitFullscreen();
            }
          },
        });
      }
      pswp?.ui.registerElement({
        name: 'bls--close',
        order: 20,
        isButton: true,
        tagName: 'button',
        html: '<svg width="13" height="13" viewBox="0 0 13 13" fill="none" class="transition"><use href="#icon-close"></use></svg>',
        onInit: (el) => {
          el.setAttribute('aria-label', 'Close full screen view');
          el.setAttribute('title', 'Close');
        },
        onClick: () => {
          pswp.close();
        },
      });
      pswp?.ui.registerElement({
        name: 'bottomBar',
        className: 'pswp__bottom-bar',
        appendTo: 'wrapper',
        onInit: (el, pswp) => {
          let next, prev, counter;
          next = document.createElement('button');
          next.setAttribute('type', 'button');
          next.className = 'pswp__button pswp__button-next';
          next.innerHTML = `<svg width="6" height="11" fill="none">
              <use href="#icon-next"></use>
            </svg>`;
          next.onclick = () => {
            pswp.next();
          };
          el.appendChild(next);

          counter = document.createElement('span');
          counter.className = 'pswp__counter';
          pswp.on('change', () => {
            counter.innerText =
              pswp.currIndex +
              1 +
              pswp.options.indexIndicatorSep +
              pswp.getNumItems();
          });
          el.appendChild(counter);

          prev = document.createElement('button');
          prev.setAttribute('type', 'button');
          prev.className = 'pswp__button pswp__button-prev';
          prev.innerHTML = `<svg width="6" height="11" fill="none">
              <use href="#icon-back"></use>
            </svg>`;
          prev.onclick = () => {
            pswp.prev();
          };
          el.appendChild(prev);
        },
      });
    });
  }
  destroyDrift() {
    if (this.drifts && this.drifts.length) {
      this.drifts.forEach((d) => {
        if (d && typeof d.destroy === 'function') d.destroy();
      });
    }
    this.drifts = [];
    this.drift = null;
  }
  initDrift() {
    if (this.drift !== null || typeof Drift === 'undefined') return;
    var allTriggers = this.querySelectorAll('.drift-trigger');
    var featuredProduct = this.closest('.sec__featured-product');
    var paneContainer = featuredProduct
      ? featuredProduct.querySelector('.zoom-external-area')
      : null;
    if (allTriggers.length != 0) {
      allTriggers.forEach((trigger) => {
        const inlineContainer = trigger.closest('[data-pane-container]');
        const zoomOption = this.zoomOption;
        let instance;
        if (zoomOption === 'inner-2') {
          instance = new Drift(trigger, {
            inlinePane: zoomOption === 'inner-2',
            zoomFactor: 3,
            containInline: paneContainer,
            paneContainer: inlineContainer,
            hoverBoundingBox: zoomOption === 'external',
            onShow: function () {
              inlineContainer.classList.add('relative');
            },
            onHide: function () {
              inlineContainer.classList.remove('relative');
            },
          });
        } else {
          instance = new Drift(trigger, {
            inlinePane: zoomOption,
            zoomFactor: 5,
            containInline: !!paneContainer,
            paneContainer:
              zoomOption === 'external' ? paneContainer : inlineContainer,
            hoverBoundingBox: zoomOption === 'external',
            onShow: function () {
              inlineContainer.classList.add('relative');
            },
            onHide: function () {
              inlineContainer.classList.remove('relative');
            },
          });
        }
        this.drift = instance;
        this.drifts.push(instance);
      });
    }
  }
  responsive() {
    if (this.shouldUseDrift()) {
      this.initDrift();
    } else {
      this.destroyDrift();
    }
    if (this.canOpenLightbox()) {
      this.initLightBox();
    }
    this.updateSwiper();
  }

  updateSwiper() {
    if (typeof Swiper !== 'undefined') {
      const thumbSlide = this.querySelector('.thumbnail-slide');
      if (thumbSlide && thumbSlide.swiper) {
        thumbSlide.swiper.update();
      }
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    this.removeEventListener('dblclick', this._onDblClick);
    this.removeEventListener('pointerdown', this._onPointerDown);
    this.removeEventListener('pointerup', this._onPointerUp);
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('load', this._onResize);
    if (this.lightbox !== null) {
      this.lightbox.destroy();
      this.lightbox = null;
    }
    this.destroyDrift();
  }
}
customElements.define('zoom-action', ZoomAction);
